// Self-heal: gỡ Avatar block CŨ sót lại trong .gitignore của repo (ticket #138
// phase-03) — đích ghi mới đã chuyển sang <git-common-dir>/info/exclude
// (phase-02). BACKUP TRƯỚC KHI SỬA (bất biến #129, không ngoại lệ): copy
// .gitignore sang backupDir rồi mới ghi/xoá; copy fail → KHÔNG sửa file.
//
// `onlyRepos` BẮT BUỘC (không optional, không mặc định "tất cả") — chỉ gỡ rào
// cũ ở repo ĐÃ dựng xong rào mới (info/exclude). Gỡ trước khi rào mới sẵn sàng
// = mất ignore VĨNH VIỄN (`git add -A` nuốt .claude/). Call site (sync.ts /
// doctor.ts) PHẢI truyền `patchResult.patched` từ patch-all-repos-git-exclude.ts
// — KHÔNG có mảng nào khác hợp lệ.
import { copyFile, readFile, rm, writeFile } from "node:fs/promises";
import { basename, join } from "node:path";
import { stripAvatarBlockFromContent } from "./avatar-block-text-ops.js";
import { ensureDir, pathExists } from "./filesystem-helpers.js";
import { readReposManifest } from "./workspace-repos-manifest-store.js";

export interface StripAvatarBlockFromReposGitignoreOptions {
  onlyRepos: string[]; // BẮT BUỘC — xem đầu file.
  backupDir?: string;
}

export interface StripAvatarBlockFromReposGitignoreResult {
  stripped: string[]; // đã gỡ block (kể cả ca file bị XOÁ — xem removedGitignore)
  // Subset của `stripped` — repo mà .gitignore RỖNG sau khi gỡ block nên bị XOÁ
  // hẳn (D3, tiền lệ execute-uninstall-deletion.ts:73-75). Tách riêng CHỈ để
  // call site (sync.ts) in log XOÁ FILE rõ ràng (bắt buộc — im lặng xoá file
  // người dùng là không chấp nhận được); về dữ liệu repo này vẫn nằm trong
  // `stripped`.
  removedGitignore: string[];
  noBlock: string[]; // .gitignore không có marker Avatar (idempotent — lần 2)
  noGitignore: string[]; // repo không có .gitignore
  corrupted: string[]; // marker hỏng (START không END / END trước START) — KHÔNG đụng
  skippedExcludeNotWritten: string[]; // repo.name ∉ onlyRepos — KHÔNG đụng file (B1)
  missing: string[]; // repo trong manifest nhưng thiếu folder
  failed: { name: string; error: string }[];
  backups: { name: string; backupPath: string }[];
}

function defaultBackupDir(workspaceRoot: string): string {
  return join(workspaceRoot, ".claude", "_backup");
}

export async function stripAvatarBlockFromReposGitignore(
  workspaceRoot: string,
  opts: StripAvatarBlockFromReposGitignoreOptions,
): Promise<StripAvatarBlockFromReposGitignoreResult> {
  const repos = await readReposManifest(workspaceRoot);
  const backupDir = opts.backupDir ?? defaultBackupDir(workspaceRoot);
  const onlyRepos = new Set(opts.onlyRepos);

  const stripped: string[] = [];
  const removedGitignore: string[] = [];
  const noBlock: string[] = [];
  const noGitignore: string[] = [];
  const corrupted: string[] = [];
  const skippedExcludeNotWritten: string[] = [];
  const missing: string[] = [];
  const failed: { name: string; error: string }[] = [];
  const backups: { name: string; backupPath: string }[] = [];

  for (const r of repos) {
    // B1 — gác BẮT BUỘC, kiểm TRƯỚC mọi thứ khác: chỉ gỡ ở repo đã dựng xong
    // rào mới (info/exclude). KHÔNG đụng file của repo ngoài onlyRepos —
    // gỡ nhầm ở đây là mất ignore vĩnh viễn.
    if (!onlyRepos.has(r.name)) {
      skippedExcludeNotWritten.push(r.name);
      continue;
    }

    const repoPath = join(workspaceRoot, r.path);
    if (!(await pathExists(repoPath))) {
      missing.push(r.name);
      continue;
    }

    const gitignorePath = join(repoPath, ".gitignore");
    try {
      if (!(await pathExists(gitignorePath))) {
        noGitignore.push(r.name);
        continue;
      }

      const existing = await readFile(gitignorePath, "utf8");
      const { content, status } = stripAvatarBlockFromContent(existing);

      if (status === "no-marker") {
        noBlock.push(r.name);
        continue;
      }
      if (status === "corrupted") {
        corrupted.push(r.name);
        continue;
      }
      if (content === null) {
        // Không nên xảy ra (status "stripped" luôn kèm content non-null ở
        // avatar-block-text-ops.ts) — giữ fail-soft nếu hợp đồng đó đổi sau này.
        failed.push({
          name: r.name,
          error: "stripAvatarBlockFromContent trả content=null cho status=stripped (bất thường)",
        });
        continue;
      }

      // status === "stripped" — BACKUP TRƯỚC KHI SỬA (bất biến, không ngoại lệ).
      await ensureDir(backupDir);
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      const backupPath = join(backupDir, `gitignore-${basename(r.name)}-${ts}`);
      await copyFile(gitignorePath, backupPath);

      if (content === "") {
        await rm(gitignorePath, { force: true });
        removedGitignore.push(r.name);
      } else {
        await writeFile(gitignorePath, content, "utf8");
      }
      stripped.push(r.name);
      backups.push({ name: r.name, backupPath });
    } catch (err) {
      failed.push({ name: r.name, error: err instanceof Error ? err.message : String(err) });
    }
  }

  return {
    stripped,
    removedGitignore,
    noBlock,
    noGitignore,
    corrupted,
    skippedExcludeNotWritten,
    missing,
    failed,
    backups,
  };
}
